/**
 * User Service
 * Handles API calls for user/buyer management
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class UserService {
  // Get auth token
  getAuthToken() {
    const token = localStorage.getItem('adminToken');
    return token ? `Bearer ${token}` : '';
  }

  // Get all buyers with filters
  async getAllBuyers(params = {}) {
    try {
      const response = await axios.get(`${API_URL}/admin/buyers`, {
        params,
        headers: {
          'Authorization': this.getAuthToken()
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch buyers' };
    }
  }

  // Get pending buyers
  async getPendingBuyers() {
    try {
      const response = await axios.get(`${API_URL}/admin/buyers/pending`, {
        headers: {
          'Authorization': this.getAuthToken()
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch pending buyers' };
    }
  }

  // Get single buyer
  async getBuyer(id) {
    try {
      const response = await axios.get(`${API_URL}/admin/buyers/${id}`, {
        headers: {
          'Authorization': this.getAuthToken()
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch buyer details' };
    }
  }

  // Approve buyer
  async approveBuyer(id) {
    try {
      const response = await axios.put(
        `${API_URL}/admin/buyers/${id}/approve`,
        {},
        {
          headers: {
            'Authorization': this.getAuthToken()
          }
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to approve buyer' };
    }
  }

  // Reject buyer
  async rejectBuyer(id, reason) {
    try {
      const response = await axios.put(
        `${API_URL}/admin/buyers/${id}/reject`,
        { reason },
        {
          headers: {
            'Authorization': this.getAuthToken(),
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to reject buyer' };
    }
  }

  // Toggle buyer active status
  async toggleBuyerActive(id) {
    try {
      const response = await axios.patch(
        `${API_URL}/admin/buyers/${id}/toggle-active`,
        {},
        {
          headers: {
            'Authorization': this.getAuthToken()
          }
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to toggle buyer status' };
    }
  }

  // Update buyer
  async updateBuyer(id, data) {
    try {
      const response = await axios.put(
        `${API_URL}/admin/buyers/${id}`,
        data,
        {
          headers: {
            'Authorization': this.getAuthToken(),
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update buyer' };
    }
  }

  // Delete buyer
  async deleteBuyer(id) {
    try {
      const response = await axios.delete(`${API_URL}/admin/buyers/${id}`, {
        headers: {
          'Authorization': this.getAuthToken()
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete buyer' };
    }
  }

  // Get buyer statistics
  async getBuyerStats() {
    try {
      const response = await axios.get(`${API_URL}/admin/buyers/stats`, {
        headers: {
          'Authorization': this.getAuthToken()
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch buyer statistics' };
    }
  }
}

export default new UserService();
